import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import {
  FaEye, FaQrcode, FaUsers, FaBoxOpen, FaTrophy, FaClock, FaCalendarAlt,
  FaChartLine, FaUserCheck, FaSearch, FaFilter, FaDownload, FaSpinner, FaChartBar
} from 'react-icons/fa';

const ShowroomAnalytics = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    activityType: 'all',
    userId: '',
    page: 1,
    limit: 20
  });
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 10000,
    withCredentials: true,
  });

  // Add token to headers and create a helper function for authenticated requests
  const makeAuthenticatedRequest = async (method, url, options = {}) => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      throw new Error('No authentication token found');
    }
    return api[method](url, options);
  };

  // Add token to headers on component mount and when token changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [user]); // Re-run when user changes

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await makeAuthenticatedRequest('get', '/company-head/showroom/dashboard', { params });
      setDashboardData(response.data.dashboard);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.message === 'No authentication token found') {
        Swal.fire({
          title: 'Authentication Error',
          text: 'Please log in again to access this feature.',
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch activities
  const fetchActivities = async () => {
    try {
      setActivitiesLoading(true);
      const params = { ...filters };
      
      const response = await makeAuthenticatedRequest('get', '/company-head/showroom/activities', { params });
      setActivities(response.data.activities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      if (error.message === 'No authentication token found') {
        Swal.fire({
          title: 'Authentication Error',
          text: 'Please log in again to access this feature.',
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      }
    } finally {
      setActivitiesLoading(false);
    }
  };

  // Fetch employees for filter dropdown
  const fetchEmployees = async () => {
    try {
      const params = {};
      
      // Add companyId for Admin/SuperAdmin if selected
      if (['Admin', 'SuperAdmin'].includes(user?.role) && filters.companyId) {
        params.companyId = filters.companyId;
      }
      const response = await api.get(('/employees/company2/'+ user.user.company), { params });
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    if (user && user.role === 'CompanyHead') {
      fetchDashboardData();
      fetchEmployees();
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [filters]);

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Get last 7 days for default date range
  const getLast7Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    
    setFilters(prev => ({
      ...prev,
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      activityType: 'all',
      userId: '',
      page: 1,
      limit: 20
    });
    fetchDashboardData();
    fetchActivities();
  };  // Export data
  const exportData = async () => {
    try {
      // Show confirmation with export details using SweetAlert2
      const result = await Swal.fire({
        title: 'Export Showroom Analytics Data?',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>Export Details:</strong></p>
            <ul class="text-sm">
              <li>📅 Date Range: ${filters.startDate || 'All dates'} to ${filters.endDate || 'Present'}</li>
              <li>📊 Activity Type: ${filters.activityType === 'all' ? 'All activities' : filters.activityType}</li>
              <li>👤 Employee: ${filters.userId ? employees.find(e => e._id === filters.userId)?.name || 'Selected employee' : 'All employees'}</li>
            </ul>
            <p class="mt-3 text-xs text-gray-600">This will download a comprehensive CSV file with detailed activity logs.</p>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#6b7280',
        confirmButtonText: '<i class="fas fa-download"></i> Export CSV',
        cancelButtonText: 'Cancel'
      });
      
      if (!result.isConfirmed) return;
      
      setActivitiesLoading(true);
      
      // Fetch all activities without pagination for export
      const exportParams = { 
        ...filters,
        limit: 10000, // Large limit to get all data
        page: 1 
      };
      
      // Add companyId for Admin/SuperAdmin
      if (['Admin', 'SuperAdmin'].includes(user?.role) && filters.companyId) {
        exportParams.companyId = filters.companyId;
      }
      
      const response = await makeAuthenticatedRequest('get', '/company-head/showroom/activities', { params: exportParams });
      const exportActivities = response.data.activities || [];
      
      // Check if there are activities to export
      if (exportActivities.length === 0) {
        const filtersInfo = {
          dateRange: filters.startDate || filters.endDate 
            ? `${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`
            : 'All dates',
          activityType: filters.activityType === 'all' ? 'All activities' : filters.activityType,
          employee: filters.userId 
            ? employees.find(e => e._id === filters.userId)?.name || 'Selected employee'
            : 'All employees'
        };
        
        Swal.fire({
          title: 'No Data to Export',
          html: `
            <div class="text-left">
              <p class="mb-3">No activities found matching your current filters:</p>
              <ul class="text-sm space-y-1">
                <li>📅 <strong>Date Range:</strong> ${filtersInfo.dateRange}</li>
                <li>📊 <strong>Activity Type:</strong> ${filtersInfo.activityType}</li>
                <li>👤 <strong>Employee:</strong> ${filtersInfo.employee}</li>
              </ul>
              <p class="mt-3 text-sm text-gray-600">Try adjusting your filters or clearing them to see more data.</p>
            </div>
          `,
          icon: 'info',
          confirmButtonColor: '#3b82f6'
        });
        return;
      }
      
      // Generate CSV content
      const csvContent = generateCSV(exportActivities);
      
      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Generate filename with timestamp
        const timestamp = new Date().toISOString().split('T')[0];
        const timeStr = new Date().toISOString().split('T')[1].substring(0, 8).replace(/:/g, '-');
        const filename = `Showroom-Analytics-${timestamp}-${timeStr}.csv`;
        
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Show success message with details
        // Show success message with SweetAlert2
        Swal.fire({
          title: 'Export Successful!',
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>Export Details:</strong></p>
              <ul class="text-sm">
                <li>📄 <strong>File:</strong> ${filename}</li>
                <li>📊 <strong>Records:</strong> ${exportActivities.length} activities</li>
                <li>📈 <strong>Summary:</strong> ${dashboardData ? 'Included' : 'Not available'}</li>
                <li>📅 <strong>Generated:</strong> ${new Date().toLocaleString()}</li>
              </ul>
            </div>
          `,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'Perfect!'
        });
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      
      // Handle specific error types with SweetAlert2
      if (error.message === 'No authentication token found') {
        Swal.fire({
          title: 'Authentication Error',
          text: 'No authentication token found. Please log in again.',
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      } else if (error.response?.status === 401) {
        Swal.fire({
          title: 'Authentication Error',
          text: 'Your session has expired. Please log in again.',
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      } else if (error.response?.status === 403) {
        Swal.fire({
          title: 'Access Denied',
          text: 'You do not have permission to export this data.',
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      } else if (error.response?.data?.message) {
        Swal.fire({
          title: 'Export Failed',
          text: error.response.data.message,
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      } else {
        Swal.fire({
          title: 'Export Failed',
          html: `
            <div class="text-left">
              <p><strong>Error:</strong> ${error.message || 'Unknown error'}</p>
              <p class="mt-2 text-sm text-gray-600">Please try again or contact support if the issue persists.</p>
            </div>
          `,
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      }
    } finally {
      setActivitiesLoading(false);
    }
  };

  // Generate CSV content from activities data
  const generateCSV = (activities) => {
    // Define CSV headers
    const headers = [
      'Date & Time',
      'Activity Type',
      'Employee Name',
      'Employee Email',
      'Item Name',
      'Item GUID'
    ];
    
    // Start with just the CSV headers
    let csvContent = headers.join(',') + '\n';
    
    // Add activity rows
    activities.forEach(activity => {
      const row = [
        `"${formatDate(activity.timestamp)}"`,
        `"${activity.activityType?.toUpperCase() || 'UNKNOWN'}"`,
        `"${activity.userName?.replace(/"/g, '""') || 'N/A'}"`,
        `"${activity.userEmail || 'N/A'}"`,
        `"${activity.itemName?.replace(/"/g, '""') || 'N/A'}"`,
        `"${activity.itemGUID || 'N/A'}"`
      ];
      csvContent += row.join(',') + '\n';
    });
    
    return csvContent;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
        <span className="ml-2 text-lg">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="p-6 w-full mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Showroom Analytics</h1>
        <div className="flex items-center justify-between">
          <p className="text-gray-600">Track item views and QR scans by your team</p>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              user?.role === 'SuperAdmin' ? 'bg-red-100 text-red-800' :
              user?.role === 'Admin' ? 'bg-orange-100 text-orange-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              ‍💼 Company Head
            </span>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-gray-500" />
            <label className="text-sm font-medium">From:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="border rounded px-3 py-1"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">To:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="border rounded px-3 py-1"
            />
          </div>

          <button
            onClick={getLast7Days}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            Last 7 Days
          </button>

          <button
            onClick={clearFilters}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Clear Filters
          </button>

          <button
            onClick={fetchDashboardData}
            className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Search
          </button>

          <button
            onClick={exportData}
            disabled={activitiesLoading}
            className={`px-4 py-1 rounded flex items-center ${
              activitiesLoading 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {activitiesLoading ? (
              <>
                <FaSpinner className="animate-spin mr-1" />
                Exporting...
              </>
            ) : (
              <>
                <FaDownload className="mr-1" />
                Quick Export
              </>
            )}
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      {dashboardData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <FaChartLine className="text-2xl text-blue-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Total Activities</p>
                  <p className="text-2xl font-bold">{dashboardData.overview.totalActivities}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <FaEye className="text-2xl text-green-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Item Views</p>
                  <p className="text-2xl font-bold">{dashboardData.overview.totalViews}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <FaQrcode className="text-2xl text-purple-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">QR Scans</p>
                  <p className="text-2xl font-bold">{dashboardData.overview.totalScans}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <FaUsers className="text-2xl text-orange-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Active Users</p>
                  <p className="text-2xl font-bold">{dashboardData.overview.uniqueUsers}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <FaBoxOpen className="text-2xl text-teal-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Items Accessed</p>
                  <p className="text-2xl font-bold">{dashboardData.overview.uniqueItemsAccessed}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Items and Employees */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top Items */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold flex items-center">
                  <FaTrophy className="text-yellow-500 mr-2" />
                  Most Accessed Items
                </h2>
              </div>
              <div className="p-4">
                {dashboardData.topItems && dashboardData.topItems.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.topItems.slice(0, 8).map((item, index) => (
                      <div key={item.itemId} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                        <div className="flex items-center">
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-3">
                            #{index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{item.itemName}</p>
                            <p className="text-xs text-gray-500">GUID: {item.itemGUID}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{item.totalAccess}</p>
                          <p className="text-xs text-gray-500">
                            {item.views} views, {item.scans} scans
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaChartBar className="text-4xl mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No item access data available</p>
                    <p className="text-xs">Items will appear here once employees start viewing or scanning them</p>
                  </div>
                )}
              </div>
            </div>

            {/* Top Employees */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold flex items-center">
                  <FaUserCheck className="text-green-500 mr-2" />
                  Most Active Employees
                </h2>
              </div>
              <div className="p-4">
                {dashboardData.employeeStats && dashboardData.employeeStats.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.employeeStats.slice(0, 8).map((employee, index) => (
                      <div key={employee.userId} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                        <div className="flex items-center">
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-3">
                            #{index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{employee.userName}</p>
                            <p className="text-xs text-gray-500">{employee.userEmail}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{employee.totalActivities}</p>
                          <p className="text-xs text-gray-500">
                            {employee.views} views, {employee.scans} scans
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaUsers className="text-4xl mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No employee activity data available</p>
                    <p className="text-xs">Employee stats will appear here once they start using the showroom</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Recent Activities */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold flex items-center">
              <FaClock className="text-blue-500 mr-2" />
              Recent Activities
            </h2>
            <div className="flex items-center gap-4">
              {/* Activity Type Filter */}
              <Select
                value={{ 
                  value: filters.activityType, 
                  label: filters.activityType === 'all' ? 'All Activities' : 
                         filters.activityType === 'view' ? 'Views Only' : 'Scans Only' 
                }}
                onChange={(selectedOption) => setFilters(prev => ({ 
                  ...prev, 
                  activityType: selectedOption.value, 
                  page: 1 
                }))}
                options={[
                  { value: 'all', label: 'All Activities' },
                  { value: 'view', label: 'Views Only' },
                  { value: 'scan', label: 'Scans Only' }
                ]}
                className="min-w-[140px]"
                classNamePrefix="react-select"
                isSearchable={false}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '32px',
                    fontSize: '14px',
                    borderColor: '#d1d5db',
                  }),
                  option: (base) => ({
                    ...base,
                    fontSize: '14px',
                  }),
                }}
              />

              {/* Employee Filter */}
              <Select
                value={employees.find(emp => emp._id === filters.userId) ? 
                  { value: employees.find(emp => emp._id === filters.userId)._id, 
                    label: `${employees.find(emp => emp._id === filters.userId).name} (${employees.find(emp => emp._id === filters.userId).id})` } : null}
                onChange={(selectedOption) => setFilters(prev => ({ 
                  ...prev, 
                  userId: selectedOption ? selectedOption.value : '', 
                  page: 1 
                }))}
                options={[
                  { value: '', label: 'All Employees' },
                  ...employees.map(emp => ({ 
                    value: emp._id, 
                    label: `${emp.name} (${emp.id})` 
                  }))
                ]}
                placeholder="Select Employee"
                isClearable
                className="min-w-[200px]"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '32px',
                    fontSize: '14px',
                    borderColor: '#d1d5db',
                  }),
                  option: (base) => ({
                    ...base,
                    fontSize: '14px',
                  }),
                }}
              />

              <div className="flex items-center gap-2">
                <button
                  onClick={exportData}
                  disabled={activitiesLoading}
                  className={`px-3 py-1 rounded text-sm flex items-center ${
                    activitiesLoading 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title="Export comprehensive CSV report with summary statistics and detailed activity logs"
                >
                  {activitiesLoading ? (
                    <>
                      <FaSpinner className="animate-spin mr-1" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FaDownload className="mr-1" />
                      Export CSV
                    </>
                  )}
                </button>
                
                {!activitiesLoading && (
                  <span className="text-xs text-gray-500">
                    Includes summary & detailed logs
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4">
          {activitiesLoading ? (
            <div className="text-center py-4">
              <FaSpinner className="animate-spin text-2xl text-blue-600 mx-auto mb-2" />
              <p>Loading activities...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Time</th>
                    <th className="text-left py-2">Type</th>
                    <th className="text-left py-2">Employee</th>
                    <th className="text-left py-2">Item</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity) => (
                    <tr key={activity._id} className="border-b hover:bg-gray-50">
                      <td className="py-2 text-sm">
                        {formatDate(activity.timestamp)}
                      </td>
                      <td className="py-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          activity.activityType === 'view' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {activity.activityType === 'view' ? <FaEye className="mr-1" /> : <FaQrcode className="mr-1" />}
                          {activity.activityType}
                        </span>
                      </td>
                      <td className="py-2 text-sm">
                        <div>
                          <p className="font-medium">{activity.userName}</p>
                          <p className="text-xs text-gray-500">{activity.userEmail}</p>
                        </div>
                      </td>
                      <td className="py-2 text-sm">
                        <div>
                          <p className="font-medium">{activity.itemName}</p>
                          <p className="text-xs text-gray-500">GUID: {activity.itemGUID}</p>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {activities.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <FaSearch className="text-4xl mx-auto mb-2" />
                  <p>No activities found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowroomAnalytics;